import { Layout, Rect, makeScene2D, type Node } from "@revideo/2d";
import { useScene } from "@revideo/core";
import { ClipSchema, cutAt, isFullscreenOpener, openerActive, secondLineFor, type Clip } from "@hudhud/core";
import { COLORS, FPS, HEIGHT, OPENER_EXIT_FRAMES, WIDTH, boundaryFrames, frameToMs, logoFor, msToFrame, openerHoldFrames, reelDurationFrames, speechDurationMs, type ReelProps } from "@hudhud/reel/math";
import { makeClock, onOff, type Clock, type Span } from "./clock";
import { ensureFonts } from "./fonts";
import { stillNode, videoDecoder, videoLayer } from "./video";
import { captionsLayer } from "./captions";
import { logoLayer } from "./logo";
import { progressLayer } from "./progress";
import { barsLayer, directedLayers, grainLayer } from "./effects";
import { overlaysLayer } from "./overlays";
import { behindTexts } from "./behind";
import { fullscreenOpener, overlayOpener } from "./opener";
import { endCardNode } from "./endcard";
import { arriveWith, leaveWith } from "./transitions";
import { coverOverlay } from "./cover";

/** Something to draw when no clip was handed in (the editor opening the project on its own). */
const DEFAULT_PROPS: ReelProps = {
  clip: ClipSchema.parse({ id: "preview", videoId: "preview", sourceStartMs: 0, sourceEndMs: 5000, segments: [{ inMs: 0, outMs: 5000 }], hook: { text: "Preview" } }),
  mediaUrl: "", mediaStartMs: 0, logoUrl: "", fontsBaseUrl: "",
};

/** Every font family the clip draws with, so they are all in before the first frame. */
function fontsUsed(clip: Clip): string[] {
  const seconds = clip.captions.map((c) => secondLineFor(clip, c.id)?.font).filter((f): f is string => !!f);
  const behind = (clip.behindTexts ?? []).map((t) => t.font).filter((f): f is string => !!f);
  return [...new Set(["Inter Tight", clip.style.font, clip.hook.font, clip.endCard.font, ...seconds, ...behind])];
}

const keep = (nodes: (Node | null)[]) => nodes.filter((n): n is Node => n !== null);

/** Remotion's `<Sequence>`: a layer that exists for its own frames, with its own clock starting at 0. */
function sequence(clock: Clock, span: Span, build: (frame: () => number) => Node | null): Node | null {
  const frame = () => clock.frame() - span.from;
  const child = build(frame);
  return child ? new Layout({ width: WIDTH, height: HEIGHT, opacity: () => onOff(clock, span), children: [child] }) : null;
}

/**
 * The reel: a full-screen opener, the cut video with its captions and chrome, then the end card — the layout of Reel.tsx, drawn
 * on a canvas. One clock is set per frame; every layer is a function of it; the driver adds and removes the pieces that only
 * exist for their own frames. The audio is mixed by the server from the same maths; nothing here makes a sound.
 */
export const reelScene = makeScene2D("reel", function* (view) {
  const props = useScene().variables.get<ReelProps>("props", DEFAULT_PROPS)();
  const clip = ClipSchema.parse(props.clip);
  const lite = !!props.lite;
  videoDecoder.current = props.player ? "web" : "ffmpeg";
  const { mediaUrl, mediaStartMs, logoUrl, logoSet, cutoutUrl, overlaysBaseUrl, settleOpener } = props;
  ensureFonts(props.fontsBaseUrl, fontsUsed(clip));

  const clock = makeClock(FPS);
  const frame = clock.frame;
  const speechFrames = Math.max(1, msToFrame(speechDurationMs(clip)));
  const hold = openerHoldFrames(clip);
  const total = reelDurationFrames(clip);
  const opener = openerActive(clip);
  const fullscreen = opener && isFullscreenOpener(clip.hook.style);
  const delayFrames = msToFrame(clip.hook.delayMs);
  // a transition at either end of the video: the opener leaves as the video arrives, and the video's last frame holds as the end card arrives
  const inFrames = fullscreen ? boundaryFrames(clip, "opener") : 0, inStyle = cutAt(clip, -1).style;
  const outFrames = boundaryFrames(clip, "end"), outStyle = cutAt(clip, clip.segments.length - 1).style;
  const openerFrames = delayFrames + msToFrame(clip.hook.durationMs) + (inFrames > 0 ? inFrames : clip.hook.transition === "cut" ? 0 : OPENER_EXIT_FRAMES);
  const speechSpan: Span = { from: hold, dur: speechFrames };

  view.add(new Rect({ width: WIDTH, height: HEIGHT, fill: COLORS.ink }));
  const video = videoLayer({
    clip, mediaUrl, mediaStartMs, cutoutUrl, lite, frame, from: hold,
    behind: (pieceFrom, pieceFrames, pieceFrame) => behindTexts(clip, pieceFrom, pieceFrames, pieceFrame),
  });

  if (props.cover) {
    // the thumbnail: the chosen still with the title, nothing that moves
    view.add(new Layout({ width: WIDTH, height: HEIGHT, opacity: () => onOff(clock, speechSpan), children: keep([video.node, lite ? null : grainLayer(clip.effects.grain)]) }));
    view.add(coverOverlay(clip, logoUrl));
  } else {
    const openerSeq = fullscreen ? sequence(clock, { from: 0, dur: Math.max(1, openerFrames) }, (f) =>
      leaveWith(inStyle, hold, inFrames, 1, lite, f,
        fullscreenOpener({ clip, logoUrl: logoFor(logoSet, !(clip.hook.bg === "ink" || clip.hook.bg === "green"), logoUrl), mediaUrl, mediaStartMs, frame: f, fps: FPS, settled: settleOpener, noExit: inFrames > 0, lite }))) : null;
    // with a transition out of the opener the video arrives on top of it, as a piece arrives over the one before
    if (inFrames > 0 && openerSeq) view.add(openerSeq);
    view.add(sequence(clock, speechSpan, (f) => arriveWith(inStyle, inFrames, 1, lite, f, new Layout({ width: WIDTH, height: HEIGHT, children: keep([
      video.node,
      directedLayers(clip, clip.hook.accentColor, f, lite),
      overlaysLayer({ clip, baseUrl: overlaysBaseUrl, frame, from: hold }),
      lite ? null : grainLayer(clip.effects.grain),
      barsLayer(clip.effects.bars),
      captionsLayer({ clip, frame, from: hold, fps: FPS, lite }),
      opener && !fullscreen ? sequence(clock, { from: hold + delayFrames, dur: msToFrame(clip.hook.durationMs) + 1 }, (of) => overlayOpener({ clip, frame: of, fps: FPS, settled: settleOpener })) : null,
      logoLayer({ clip, logoUrl, frame, from: hold, lite }),
      clip.brand.progressBar ? progressLayer({ clip, frame, from: hold, totalFrames: speechFrames }) : null,
    ]) }))) as Node);
    if (inFrames === 0 && openerSeq) view.add(openerSeq);
    // the video's last frame stays under the arriving end card for the transition's overlap
    if (outFrames > 0) view.add(sequence(clock, { from: hold + speechFrames, dur: outFrames }, (f) =>
      leaveWith(outStyle, 0, outFrames, 1, lite, f, new Layout({ width: WIDTH, height: HEIGHT, children: keep([
        stillNode({ clip, mediaUrl, mediaStartMs, videoMs: frameToMs(speechFrames - 1), cutoutUrl, lite }),
        lite ? null : grainLayer(clip.effects.grain),
        barsLayer(clip.effects.bars),
      ]) }))) as Node);
    if (clip.endCard.enabled) view.add(sequence(clock, { from: hold + speechFrames, dur: Math.max(1, total - hold - speechFrames) }, (f) =>
      arriveWith(outStyle, outFrames, 1, lite, f,
        endCardNode({ clip, logoUrl: logoFor(logoSet, clip.endCard.bg === "gold" || clip.endCard.bg === "white", logoUrl), mediaUrl, mediaStartMs, frame: f, fps: FPS, lite }))) as Node);
  }

  // Motion Canvas draws the scene's state after each yield AND its final state when the generator returns, so `total` frames
  // are `total - 1` yields: frame f is prepared, then handed over — except the last, which is left standing.
  for (let f = 0; f < total; f++) {
    clock.set(f);
    video.tick(f);
    if (f < total - 1) yield;
  }
});
